import { useLanguage } from "contexts/LocalizationContext";
import { useStoryblokState } from "@storyblok/react";
import type {
  AirlineRankingTemplateProps,
  AirportRankingTemplateProps,
} from "modules/storyblok/templates/AirHelpScore/AirHelpScoreWrapper";
import type { FlexibleTemplateProps } from "modules/storyblok/templates/FlexibleTemplate";
import type { HelpHomeTemplateProps } from "modules/storyblok/templates/HelpHomeTemplate";
import type { IframeTemplateProps } from "modules/storyblok/templates/IframeTemplate";
import type { ReferralTemplateProps } from "modules/storyblok/templates/ReferralTemplate";
import type { SideBarTemplateProps } from "modules/storyblok/templates/SideBarTemplate";
import type { StoryblokTemplateProps, TemplateContent } from "types/storyblok";
import dynamic from "next/dynamic";
import { useMemo } from "react";
import type { BasicTemplateProps } from "../BasicTemplate";
import type { BlogHomeTemplateProps } from "../BlogHomeTemplate";
import type { BlogPostTemplateProps } from "../BlogPostTemplate";
import type { PressHomeTemplateProps } from "../PressHomeTemplate";
import { setVariables } from "./utils/setDynamicVariables";
import { GlobalTemplate } from "../GlobalTemplate";

const BasicTemplate = dynamic<BasicTemplateProps>(() =>
  import("modules/storyblok/templates/BasicTemplate").then((m) => m.BasicTemplate)
);
const BlogPostTemplate = dynamic<BlogPostTemplateProps>(() =>
  import("modules/storyblok/templates/BlogPostTemplate").then((m) => m.BlogPostTemplate)
);
const BlogHomeTemplate = dynamic<BlogHomeTemplateProps>(() =>
  import("modules/storyblok/templates/BlogHomeTemplate").then((m) => m.BlogHomeTemplate)
);
const PressHomeTemplate = dynamic<PressHomeTemplateProps>(() =>
  import("modules/storyblok/templates/PressHomeTemplate").then((m) => m.PressHomeTemplate)
);
const FlexibleTemplate = dynamic<FlexibleTemplateProps>(() =>
  import("modules/storyblok/templates/FlexibleTemplate").then((m) => m.FlexibleTemplate)
);
const ReferralTemplate = dynamic<ReferralTemplateProps>(() =>
  import("modules/storyblok/templates/ReferralTemplate").then((m) => m.ReferralTemplate)
);
const SideBarTemplate = dynamic<SideBarTemplateProps>(() =>
  import("modules/storyblok/templates/SideBarTemplate").then((m) => m.SideBarTemplate)
);
const IframeTemplate = dynamic<IframeTemplateProps>(() =>
  import("modules/storyblok/templates/IframeTemplate").then((m) => m.IframeTemplate)
);
const HelpHomeTemplate = dynamic<HelpHomeTemplateProps>(() =>
  import("modules/storyblok/templates/HelpHomeTemplate").then((m) => m.HelpHomeTemplate)
);
const AirlineRanking = dynamic<AirlineRankingTemplateProps>(() =>
  import("modules/storyblok/templates/AirHelpScore/AirlineRanking").then((m) => m.AirlineRanking)
);
const AirportRanking = dynamic<AirportRankingTemplateProps>(() =>
  import("modules/storyblok/templates/AirHelpScore/AirportRanking").then((m) => m.AirportRanking)
);
const ConfigurationBasedHomeTemplate = dynamic<any>(() =>
  import("modules/storyblok/templates/ConfigurationBasedHomeTemplate").then((m) => m.ConfigurationBasedHomeTemplate)
);

const StoryblokTemplate = ({ ...props }: StoryblokTemplateProps) => {
  const { story } = props;

  const storyContent = useStoryblokState<TemplateContent>(story, { resolveRelations: ["global_reference.reference"] });

  const bridgedProps = { ...props, story: storyContent } as StoryblokTemplateProps;
  const { templateComponent } = bridgedProps;

  switch (templateComponent) {
    case "basicTemplate": {
      return <BasicTemplate {...bridgedProps} />;
    }
    case "blogPost": {
      return <BlogPostTemplate {...bridgedProps} />;
    }
    case "blogHome": {
      return <BlogHomeTemplate {...bridgedProps} />;
    }
    case "pressHome": {
      return <PressHomeTemplate {...bridgedProps} />;
    }
    case "flexibleTemplate": {
      return <FlexibleTemplate {...bridgedProps} />;
    }
    case "referralTemplate": {
      return <ReferralTemplate {...bridgedProps} />;
    }
    case "sideBarTemplate": {
      return <SideBarTemplate {...bridgedProps} />;
    }
    case "iframeTemplate": {
      return <IframeTemplate {...bridgedProps} />;
    }
    case "helpHome": {
      return <HelpHomeTemplate {...bridgedProps} />;
    }
    case "airlineRanking": {
      return <AirlineRanking {...bridgedProps} />;
    }
    case "airportRanking": {
      return <AirportRanking {...bridgedProps} />;
    }
    case "globalTemplate": {
      return <GlobalTemplate {...bridgedProps} />;
    }
    // @ts-ignore
    case "configurationBasedHomeTemplate": {
      // @ts-ignore
      return <ConfigurationBasedHomeTemplate {...bridgedProps} />;
    }

    default:
      throw new Error(`Unexpected Template: ${templateComponent}`);
  }
};

export const StoryblokTemplateWrapper = (props: StoryblokTemplateProps) => {
  const language = useLanguage();
  const content = useMemo(() => setVariables(props, language), [language, props]);

  return <StoryblokTemplate {...content} />;
};
